import { Link } from 'react-router-dom';
import { useState, FormEvent } from 'react';
import { COMPANY, SERVICES } from '../../mocks/siteData';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const FORM_URL = 'https://readdy.ai/api/form/d75ah9mcsbv12mkudin0';

interface FinalCTAProps {
  title?: string;
  subtitle?: string;
  primaryLabel?: string;
  className?: string;
}

export default function FinalCTA({
  title = 'Start Your Remodeling Project With Confidence',
  subtitle = 'Your home should reflect how you live today — not how it was built years ago. Bay City Remodeling delivers the craftsmanship and accountability to make it happen.',
  className = '',
}: FinalCTAProps) {
  const { ref, visible } = useScrollAnimation();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as URLSearchParams);
    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setSubmitted(true);
        form.reset();
      } else {
        setError('Something went wrong. Please call us directly.');
      }
    } catch {
      setError('Unable to submit. Please call ' + COMPANY.phone);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      className={`relative py-28 px-6 lg:px-16 xl:px-24 overflow-hidden ${className}`}
      style={{
        backgroundImage: `url('https://public.readdy.ai/ai/img_res/2aeaab7e93ea504c2781bb63ff4e7cdb.jpg')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center 60%',
      }}
    >
      {/* Overlays */}
      <div className="absolute inset-0 bg-gradient-to-r from-stone-950/97 via-stone-950/90 to-stone-950/60" />
      <div className="absolute inset-0 bg-gradient-to-t from-stone-950/50 to-transparent" />

      <div className="relative z-10 max-w-6xl mx-auto">
        <div
          ref={ref}
          className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-start transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          {/* Left: Heading */}
          <div>
            <p className="text-accent text-xs font-semibold tracking-[0.2em] uppercase mb-5">Get Started Today</p>
            <h2 className="font-serif text-4xl lg:text-[56px] text-white leading-tight mb-6" style={{ letterSpacing: '-0.02em' }}>
              {title}
            </h2>
            <p className="text-stone-400 text-base leading-relaxed mb-10">{subtitle}</p>

            {/* Trust signals */}
            <div className="flex flex-col gap-4 mb-10">
              {[
                { icon: 'ri-shield-check-fill', text: 'Fully licensed & insured in Florida' },
                { icon: 'ri-star-fill', text: `${COMPANY.rating} stars · ${COMPANY.reviewCount} verified homeowner reviews` },
                { icon: 'ri-map-pin-2-line', text: 'Based in Brandon, FL · Serving 23+ communities' },
                { icon: 'ri-phone-fill', text: 'Call anytime: ' + COMPANY.phone },
              ].map((item) => (
                <div key={item.icon} className="flex items-center gap-3">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    <i className={`${item.icon} text-accent text-xs`} />
                  </div>
                  <span className="text-stone-400 text-sm">{item.text}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href={`tel:${COMPANY.phoneFull}`}
                className="inline-flex items-center gap-3 border border-white/20 hover:border-accent/60 bg-white/5 hover:bg-accent/10 text-white font-medium px-6 py-3.5 rounded-lg transition-all duration-300 whitespace-nowrap text-sm"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-phone-fill text-accent" />
                </div>
                Call {COMPANY.phone}
              </a>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 text-accent hover:text-accent-light text-sm font-medium transition-colors"
              >
                View Full Contact Page
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-xs" />
                </div>
              </Link>
            </div>
          </div>

          {/* Right: Mini Form */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8">
            {submitted ? (
              <div className="text-center py-12">
                <div className="w-14 h-14 rounded-full bg-accent/20 border border-accent/30 flex items-center justify-center mx-auto mb-5">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-check-line text-accent text-xl" />
                  </div>
                </div>
                <h3 className="font-serif text-2xl text-white mb-3">Request Received</h3>
                <p className="text-stone-400 text-sm leading-relaxed">
                  We&apos;ll be in touch within 24 hours. For immediate assistance, call us at {COMPANY.phone}.
                </p>
              </div>
            ) : (
              <>
                <div className="mb-7">
                  <h3 className="font-serif text-2xl text-white mb-1">Request a Free Consultation</h3>
                  <p className="text-stone-500 text-xs">No commitment · We come to your home · Brandon, FL &amp; surrounding areas</p>
                </div>
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  id="final-cta-consultation-form"
                  className="flex flex-col gap-4"
                >
                  <div>
                    <label className="block text-stone-400 text-xs font-medium mb-1.5 tracking-wide uppercase">Your Name *</label>
                    <input
                      name="name"
                      type="text"
                      required
                      placeholder="John Smith"
                      className="w-full bg-white/8 border border-white/15 focus:border-accent/60 rounded-lg px-4 py-3 text-white text-sm placeholder-stone-600 focus:outline-none transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-stone-400 text-xs font-medium mb-1.5 tracking-wide uppercase">Phone Number *</label>
                    <input
                      name="phone"
                      type="tel"
                      required
                      placeholder="(813) 555-0100"
                      className="w-full bg-white/8 border border-white/15 focus:border-accent/60 rounded-lg px-4 py-3 text-white text-sm placeholder-stone-600 focus:outline-none transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-stone-400 text-xs font-medium mb-1.5 tracking-wide uppercase">Email Address</label>
                    <input
                      name="email"
                      type="email"
                      placeholder="john@example.com"
                      className="w-full bg-white/8 border border-white/15 focus:border-accent/60 rounded-lg px-4 py-3 text-white text-sm placeholder-stone-600 focus:outline-none transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-stone-400 text-xs font-medium mb-1.5 tracking-wide uppercase">Service Interested In</label>
                    <select
                      name="service"
                      className="w-full bg-stone-900 border border-white/15 focus:border-accent/60 rounded-lg px-4 py-3 text-stone-300 text-sm focus:outline-none transition-colors cursor-pointer appearance-none"
                    >
                      <option value="">Select a service...</option>
                      {SERVICES.map((s) => (
                        <option key={s.slug} value={s.title}>{s.title}</option>
                      ))}
                      <option value="Not Sure Yet">Not Sure Yet</option>
                    </select>
                  </div>
                  {error && (
                    <p className="text-red-400 text-xs bg-red-900/20 border border-red-800/30 rounded-lg px-4 py-2.5">{error}</p>
                  )}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-accent hover:bg-accent-dark disabled:opacity-50 text-white font-semibold py-4 rounded-lg transition-colors whitespace-nowrap text-sm tracking-wide mt-1 cursor-pointer"
                  >
                    {submitting ? 'Sending Request...' : 'Request My Free Consultation'}
                  </button>
                  <p className="text-stone-600 text-xs text-center">Licensed · Insured · No-obligation consultation</p>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
